import type { Role, User } from '@/lib/auth-context';
import { type ModuleKey, TEACHER_GRANTABLE_MODULES } from '@/lib/modules';

type AccessUser = Pick<User, 'role' | 'modules' | 'isPosOperator'>;

// A POS operator (till account) passes none of these gates: it sees only the pages in
// POS_TILLS below. Base items (no module) are visible to teacher + admin. Module-gated items are
// visible to an admin whose grants include the key, or a teacher whose grants
// include the key AND the key is in the teacher-grantable allowlist. Students
// never reach the admin app.
export function canAccess(user: AccessUser | null | undefined, module?: ModuleKey): boolean {
  if (!user || user.isPosOperator) return false;
  if (!module) return user.role === 'admin' || user.role === 'teacher';
  if (user.role === 'admin') return user.modules.includes(module);
  if (user.role === 'teacher') return TEACHER_GRANTABLE_MODULES.includes(module) && user.modules.includes(module);
  return false;
}

/**
 * Visibility for a dashboard widget, mirroring the sidebar's isVisible:
 * an `adminOnly` widget needs role === 'admin'; a `staffOnly` widget bypasses
 * `canAccess` entirely (visible to admin/teacher/employee, mirroring the
 * sidebar's `staffOnly` nav items); otherwise fall back to `canAccess`
 * (base = admin+teacher, module = admin AND the grant).
 */
export function canAccessWidget(
  user: AccessUser | null | undefined,
  gate: { module?: ModuleKey; adminOnly?: boolean; staffOnly?: boolean }
): boolean {
  if (user?.isPosOperator) return false;
  if (gate.adminOnly) return user?.role === 'admin';
  if (gate.staffOnly) return !!user && (['admin', 'teacher', 'employee'] as Role[]).includes(user.role);
  return canAccess(user, gate.module);
}

/**
 * Whether this user may look at the staff half of the attendance pages.
 *
 * Teachers run these pages over students; colleagues' attendance is an admin's to read. The
 * pages hide the Students/Staff toggle rather than showing a control that would be refused,
 * and pin the choice to students — the API enforces the same rule, since the role travels as
 * a query parameter.
 */
export function canSeeStaffAttendance(user: Pick<User, 'role'> | null | undefined): boolean {
  return user?.role === 'admin';
}

/** The point-of-sale page of each module a till account can run, in the order a till lands on them. */
export const POS_TILLS: { module: ModuleKey; href: string }[] = [
  { module: 'cafeteria', href: '/cafeteria/pos' },
  { module: 'shop', href: '/shop/pos' },
];

const POS_SELF_SERVICE_PATHS = ['/my-profile'];

/** Whether a till account may open this path: its modules' POS pages and its own profile. */
export function posOperatorMayVisit(user: Pick<User, 'role' | 'modules'>, pathname: string): boolean {
  if (POS_SELF_SERVICE_PATHS.includes(pathname)) return true;
  return POS_TILLS.some(till => till.href === pathname && user.role === 'admin' && user.modules.includes(till.module));
}

/** Where a till account lands: its first POS page, or its profile when it holds no till module. */
export function posOperatorHome(user: Pick<User, 'role' | 'modules'>): string {
  return POS_TILLS.find(till => posOperatorMayVisit(user, till.href))?.href ?? '/my-profile';
}
